import React, { useMemo } from "react";
import { useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import "@companion-core/web/src/Assets/Styles/scss/recommendedList.scss";
import "@companion-core/web/src/Assets/Styles/scss/loader.scss";
import { useOnboardingStore } from "@companion-core/shared/app/Store/onboardingStore";
import { ComponentData } from "@companion-core/shared/app/Interfaces/API";
import {
  buildRecommendedListQueryArgs,
  buildRecommendedListTitle,
  resolveRecommendedListCardVariant,
  resolveRecommendedListParams,
} from "./recommendedList.utils";
import { useRecommendedListSearch } from "./hooks/useRecommendedListSearch";
import { ComponentCard } from "@companion-core/shared/app/Components/Cards/ComponentCard";
import { Button } from "@companion-core/shared/app/Components/Button";
import { FontIcon } from "@companion-core/shared/app/Components/FontIcon";
import { useMemberData } from "@companion-core/shared/app/Hooks/member";
import { useRedirectToNextStep } from "./hooks/useRecommendedList";
import { useRecommendedListHeader } from "./hooks/useRecommendedListHeader";
import {
  trackPageRecommendedList,
  trackSkipRecommendedListClick,
} from "@companion-core/shared/app/tracking/recommendedList";
import { useMountEffect } from "@companion-core/shared/app/Hooks/useMountEffect";
import ChaptersEnum from "@companion-core/shared/app/tracking/enums/chapters";

export const RecommendedList = () => {
  const { t, i18n } = useTranslation();
  const { data: member } = useMemberData();
  const [searchParams] = useSearchParams();
  const tags = useOnboardingStore((state) => state.tags);
  const contentFilters = useOnboardingStore((state) => state.contentFilters);
  const universe = useOnboardingStore((state) => state.universe);
  const universeSlug = universe?.slug ?? "";
  useRecommendedListHeader();
  const resolvedParams = useMemo(
    () =>
      resolveRecommendedListParams({
        searchParams,
        storeTags: tags,
        storeContentFilters: contentFilters,
      }),
    [searchParams, tags, contentFilters]
  );
  const lang = i18n?.languages?.[0] ?? "fr";
  const cardVariant = useMemo(
    () => resolveRecommendedListCardVariant(resolvedParams.contentType),
    [resolvedParams.contentType]
  );
  const queryArgs = useMemo(
    () => buildRecommendedListQueryArgs({ resolvedParams, lang }),
    [resolvedParams, lang]
  );
  const { data, showLoadingState, loadingKeys } = useRecommendedListSearch(queryArgs);
  const title = useMemo(
    () => buildRecommendedListTitle({ member, t }),
    [member?.salutation, member?.firstname, member?.lastname, t]
  );
  const redirectToNextStep = useRedirectToNextStep();

  useMountEffect(() => {
    trackPageRecommendedList(universeSlug);
  });

  const chapters = useMemo(
    () => ({
      chapter1: ChaptersEnum.Onboarding,
      chapter2: universeSlug,
      chapter3: "",
    }),
    [universeSlug]
  );

  const onSkipRecommendedList = () => {
    trackSkipRecommendedListClick(universeSlug);
    redirectToNextStep();
  };

  return (
    <section data-testid="recommended-list" className="recommended-list">
      <div className="recommended-list__content">
        {showLoadingState ? (
          <div className="recommended-list__loading-container">
            <div
              className="loading-spinner loading-spinner-large"
              data-testid="recommended-list__loader"
            />
            <h1 className="recommended-list__title title-4">{t(loadingKeys.title)}</h1>
            <p className="recommended-list__description body-text-6">
              {t(loadingKeys.description)}
            </p>
          </div>
        ) : data?.length && data.length > 0 ? (
          <div data-testid="recommended-list__content" className="recommended-list__results">
            <span className="recommended-list__results-title title-3">{title}</span>
            <div className="recommended-list__results-description body-text-6">
              {cardVariant === "vendor" && <FontIcon name="JP-Location" size={20} />}
              <span className="recommended-list__results-description-text body-text-6">
                {t(`blocks.recommendedList.results.${cardVariant}.description`)}
              </span>
            </div>
            <div className="recommended-list__cards">
              {data?.map((item: ComponentData, index: number) => (
                <div key={item.id} className="recommended-list__item">
                  <ComponentCard
                    component={item}
                    pageSlug="recommendation"
                    url={window.location.href}
                    chapters={chapters}
                    position={index}
                    nbItems={data.length}
                    tags={item.tags_v2 ?? []}
                    variant={cardVariant}
                    redirectQueryParams="onboarding=true"
                  />
                </div>
              ))}
            </div>
            <div className="recommended-list__later-button-container">
              <Button
                testID="recommended-list__later-button"
                type="primary"
                size="large"
                classNames={["recommended-list__later-button"]}
                label={t("blocks.recommendedList.results.later.button")}
                onClick={onSkipRecommendedList}
              />
            </div>
          </div>
        ) : (
          <>
            <p data-testid="recommended-list__fallback">Fallback logic to be Added later</p>
            <div className="recommended-list__later-button-container">
              <Button
                testID="recommended-list__later-button"
                type="primary"
                size="large"
                classNames={["recommended-list__later-button"]}
                label={t("blocks.recommendedList.results.later.button")}
                onClick={onSkipRecommendedList}
              />
            </div>
          </>
        )}
      </div>
    </section>
  );
};
